"use client";

import { FormEvent, useCallback, useEffect, useState } from "react";

import { apiBaseUrl } from "@/lib/browser-api";

type Learner = {
  id: string;
  display_name: string;
  grade: number | null;
  state: string | null;
};

function csrfToken(): string {
  return decodeURIComponent(
    document.cookie
      .split("; ")
      .find((part) => part.startsWith("lernwerk_csrf="))
      ?.split("=")[1] ?? "",
  );
}

function learnerInfo(learner: Learner): string {
  const details = [learner.grade ? `Klasse ${learner.grade}` : "Klasse offen", learner.state].filter(Boolean);
  return details.join(" · ");
}

export default function LearnerProfiles() {
  const [learners, setLearners] = useState<Learner[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "signed_out" | "error">("loading");
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/learners`, { credentials: "include" });
      if (response.status === 401) {
        setState("signed_out");
        return;
      }
      if (!response.ok) throw new Error("learners unavailable");
      setLearners((await response.json()) as Learner[]);
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => {
    const initialLoad = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(initialLoad);
  }, [load]);

  async function addLearner(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage(undefined);
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch(`${apiBaseUrl}/api/learners`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({
          display_name: form.get("display_name"),
          grade: form.get("grade") ? Number(form.get("grade")) : null,
          state: String(form.get("state") ?? "").trim().toUpperCase() || null,
        }),
      });
      if (!response.ok) throw new Error("creation unavailable");
      setShowForm(false);
      await load();
    } catch {
      setMessage("Das Profil konnte nicht angelegt werden. Bitte versuche es erneut.");
    } finally {
      setBusy(false);
    }
  }

  async function removeLearner(learner: Learner) {
    if (!window.confirm(`Profil „${learner.display_name}“ wirklich löschen?`)) return;
    setBusy(true);
    setMessage(undefined);
    try {
      const response = await fetch(`${apiBaseUrl}/api/learners/${learner.id}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-CSRF-Token": csrfToken() },
      });
      if (!response.ok) throw new Error("deletion unavailable");
      await load();
    } catch {
      setMessage("Das Profil konnte nicht gelöscht werden. Bitte versuche es erneut.");
    } finally {
      setBusy(false);
    }
  }

  if (state === "loading") return <section className="learner-section"><p className="notice">Kinderprofile werden geladen …</p></section>;
  if (state === "signed_out") return null;
  if (state === "error") return <section className="learner-section"><p className="notice">Kinderprofile sind gerade nicht erreichbar.</p></section>;

  return (
    <section className="learner-section" aria-labelledby="learner-heading">
      <div className="learner-heading">
        <div><h2 id="learner-heading">Kinderprofile</h2><p>Nur ein Name oder Spitzname genügt. Eine eigene E-Mail-Adresse ist nicht nötig.</p></div>
        <button className="button secondary" disabled={busy} onClick={() => setShowForm((visible) => !visible)} type="button">
          {showForm ? "Abbrechen" : "Kind hinzufügen"}
        </button>
      </div>
      {showForm ? (
        <form className="learner-form" onSubmit={addLearner}>
          <label>Name oder Spitzname<input name="display_name" maxLength={80} required /></label>
          <label>Klasse<select name="grade" defaultValue=""><option value="">Noch offen</option>{[1,2,3,4,5,6,7,8,9,10].map((grade) => <option key={grade} value={grade}>Klasse {grade}</option>)}</select></label>
          <label>Bundesland <input name="state" maxLength={2} placeholder="Zum Beispiel: BY" /></label>
          <button className="button primary" disabled={busy} type="submit">{busy ? "Wird gespeichert …" : "Profil speichern"}</button>
        </form>
      ) : null}
      {learners.length ? <div className="learner-list">{learners.map((learner) => <div className="learner-card" key={learner.id}><div><strong>{learner.display_name}</strong><span>{learnerInfo(learner)}</span></div><button className="learner-delete" disabled={busy} onClick={() => void removeLearner(learner)} type="button">Profil löschen</button></div>)}</div> : <p className="learner-empty">Noch kein Profil angelegt. Beim Erstellen eines Arbeitsblatts kannst du auch ohne Profil starten.</p>}
      {message ? <p className="job-error" role="status">{message}</p> : null}
    </section>
  );
}
